import React, { useState } from 'react'
import './ChartsPanel.css'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ScatterChart, Scatter, LineChart, Line } from 'recharts'
import { BarChart3, PieChart as PieChartIcon, ScatterChart as ScatterIcon, TrendingUp } from 'lucide-react'

const ChartsPanel = ({ data }) => {
  const [activeChart, setActiveChart] = useState('market-cap')

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B6B']

  const getSectorMarketCap = () => {
    const sectorData = data.reduce((acc, item) => {
      if (item.Sector && item['Market Cap']) {
        acc[item.Sector] = (acc[item.Sector] || 0) + item['Market Cap']
      }
      return acc
    }, {})

    return Object.entries(sectorData)
      .map(([sector, marketCap]) => ({
        name: sector,
        value: marketCap / 1000000000, // Convert to billions
        marketCap: marketCap
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8) // Top 8 sectors
  }

  const getTopCompanies = (metric, count = 10) => {
    return data
      .filter(item => item[metric] && item[metric] > 0)
      .sort((a, b) => b[metric] - a[metric])
      .slice(0, count)
      .map(item => ({
        name: item.Company,
        ticker: item.Ticker,
        value: parseFloat(item[metric]),
        sector: item.Sector
      }))
  }

  const getScatterData = () => {
    return data
      .filter(item => item['P/E'] && item['Dividend Yield'] && item['Market Cap'])
      .map(item => ({
        name: item.Company,
        pe: parseFloat(item['P/E']),
        dividend: parseFloat(item['Dividend Yield']),
        marketCap: item['Market Cap'] / 1000000000,
        sector: item.Sector
      }))
  }

  const getPricePerformance = () => {
    return data
      .filter(item => item['Price Today'] && item['Price 1y ago'])
      .map(item => {
        const currentPrice = parseFloat(item['Price Today'])
        const yearAgoPrice = parseFloat(item['Price 1y ago'])
        const performance = ((currentPrice - yearAgoPrice) / yearAgoPrice) * 100
        
        return {
          name: item.Company,
          ticker: item.Ticker,
          performance: performance,
          sector: item.Sector,
          currentPrice: currentPrice
        }
      })
      .sort((a, b) => b.performance - a.performance)
      .slice(0, 15)
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  const renderMarketCapChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={getSectorMarketCap()}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="name" 
          stroke="#fff"
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis 
          stroke="#fff"
          fontSize={12}
          tickFormatter={(value) => `$${value}B`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" fill="#8884d8" />
      </BarChart>
    </ResponsiveContainer>
  )

  const renderTopCompaniesChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={getTopCompanies('Market Cap')} layout="horizontal">
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          type="number"
          stroke="#fff"
          fontSize={12}
          tickFormatter={(value) => `$${value}B`}
        />
        <YAxis 
          dataKey="name" 
          type="category"
          stroke="#fff"
          fontSize={10}
          width={120}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="value" fill="#82ca9d" />
      </BarChart>
    </ResponsiveContainer>
  )

  const renderScatterChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <ScatterChart>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          type="number" 
          dataKey="pe" 
          name="P/E Ratio"
          stroke="#fff"
          fontSize={12}
        />
        <YAxis 
          type="number" 
          dataKey="dividend" 
          name="Dividend Yield"
          stroke="#fff"
          fontSize={12}
          tickFormatter={(value) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Scatter data={getScatterData()} fill="#8884d8" />
      </ScatterChart>
    </ResponsiveContainer>
  )

  const renderPerformanceChart = () => (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart data={getPricePerformance()}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
        <XAxis 
          dataKey="ticker" 
          stroke="#fff"
          fontSize={12}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis 
          stroke="#fff"
          fontSize={12}
          tickFormatter={(value) => `${value}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Bar 
          dataKey="performance" 
          fill={(entry) => entry.performance > 0 ? '#4CAF50' : '#f44336'}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const chartOptions = [
    { id: 'market-cap', label: 'Market Cap by Sector', icon: BarChart3 },
    { id: 'top-companies', label: 'Top Companies', icon: TrendingUp },
    { id: 'scatter', label: 'P/E vs Dividend', icon: ScatterIcon },
    { id: 'performance', label: 'Price Performance', icon: PieChartIcon }
  ]

  const renderChart = () => {
    switch (activeChart) {
      case 'market-cap':
        return renderMarketCapChart()
      case 'top-companies':
        return renderTopCompaniesChart()
      case 'scatter':
        return renderScatterChart()
      case 'performance':
        return renderPerformanceChart()
      default:
        return renderMarketCapChart()
    }
  }

  return (
    <div className="charts-panel">
      <div className="charts-header">
        <h3>Data Visualizations</h3>
        <div className="chart-tabs">
          {chartOptions.map(option => {
            const IconComponent = option.icon
            return (
              <button
                key={option.id}
                className={`chart-tab ${activeChart === option.id ? 'active' : ''}`}
                onClick={() => setActiveChart(option.id)}
              >
                <IconComponent className="tab-icon" />
                <span>{option.label}</span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="chart-container">
        {renderChart()}
      </div>

      <div className="chart-info">
        <p>
          {activeChart === 'market-cap' && 'Market capitalization distribution across sectors'}
          {activeChart === 'top-companies' && 'Largest companies by market capitalization'}
          {activeChart === 'scatter' && 'P/E ratio vs dividend yield correlation'}
          {activeChart === 'performance' && '1-year price performance comparison'}
        </p>
      </div>
    </div>
  )
}

export default ChartsPanel 